import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useApp } from "../context/AppContext";
import { fetchArtist, fetchProducts } from "../lib/api";
import { ProductCard } from "../components/ProductCard";
import { langField } from "../lib/i18n";

export default function ArtistDetail() {
  const { slug } = useParams();
  const { t, lang } = useApp();
  const [artist, setArtist] = useState(null);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    fetchArtist(slug).then(setArtist);
  }, [slug]);

  useEffect(() => {
    if (!artist) return;
    fetchProducts().then((all) => {
      setProducts(all.filter((p) => p.artist === artist.name));
    });
  }, [artist]);

  if (!artist) {
    return (
      <div className="max-w-[1600px] mx-auto px-5 md:px-10 py-20">
        <p className="font-mono text-xs text-kult-muted">LOADING...</p>
      </div>
    );
  }

  const isWrestler = artist.role.toLowerCase().includes("wrestler");

  return (
    <div data-testid="page-artist-detail">
      <section className="relative border-b border-kult-border">
        <div className="absolute inset-0">
          <img src={artist.image} alt={artist.name} className="w-full h-full object-cover opacity-30 grayscale" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-black/60" />
        </div>
        <div className="relative max-w-[1600px] mx-auto px-5 md:px-10 py-20 md:py-32">
          <Link to="/artists" className="inline-flex items-center font-mono text-xs tracking-[0.25em] kult-link mb-8">
            <ArrowLeft size={14} className="mr-2" /> ROSTER
          </Link>
          <p className="font-mono text-xs tracking-[0.3em] text-kult-yellow">
            {isWrestler ? "/ WRESTLER" : "/ ARTIST"}
          </p>
          <h1 className="font-display text-7xl md:text-[12rem] leading-[0.85] tracking-tighter mt-3">
            <span className="text-white">{artist.name.split("").slice(0, Math.floor(artist.name.length / 2)).join("")}</span>
            <span className="text-kult-pink">{artist.name.split("").slice(Math.floor(artist.name.length / 2)).join("")}</span>
          </h1>
          <p className="font-mono text-sm tracking-[0.25em] text-kult-yellow mt-4">
            {langField(artist, "role", lang).toUpperCase()}
          </p>
          {artist.quote && (
            <p className="font-display text-3xl md:text-5xl text-kult-pink mt-6 max-w-3xl">
              "{langField(artist, "quote", lang)}"
            </p>
          )}
        </div>
      </section>

      <section className="max-w-[1100px] mx-auto px-5 md:px-10 py-16">
        <p className="text-lg md:text-xl text-kult-muted leading-relaxed">
          {langField(artist, "bio", lang)}
        </p>
        <Link
          to={`/store/${artist.slug}`}
          className="inline-flex items-center gap-2 mt-8 bg-kult-pink text-black px-6 py-3 font-display text-2xl hover:bg-kult-yellow transition-colors"
          data-testid="artist-detail-store-cta"
        >
          ⚡ VISIT {artist.name}'S STORE
        </Link>
      </section>

      {products.length > 0 && (
        <section className="border-t border-kult-border bg-kult-black2">
          <div className="max-w-[1600px] mx-auto px-5 md:px-10 py-16">
            <p className="font-mono text-xs tracking-[0.3em] text-kult-yellow">
              / SIGNATURE LINE
            </p>
            <h2 className="font-display text-5xl md:text-7xl mt-2 mb-10">
              SHOP {artist.name}
            </h2>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
              {products.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          </div>
        </section>
      )}

      {products.length === 0 && (
        <section className="max-w-[1100px] mx-auto px-5 md:px-10 py-20 text-center">
          <p className="font-mono text-xs tracking-[0.25em] text-kult-muted">
            {lang === "pt"
              ? "EM BREVE — SIGNATURE LINE A CHEGAR."
              : "COMING SOON — SIGNATURE LINE DROPPING SOON."}
          </p>
        </section>
      )}
    </div>
  );
}
